import React, { useRef } from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  alpha,
  useTheme,
  useMediaQuery,
  TextField,
  MenuItem,
  Tooltip,
  IconButton,
} from "@mui/material";
import { formatAmount } from "../../../../../utils/hooks/dataHandler";
import {
  CameraExportButton,
  exportComponentAsImage,
} from "../../../../../utils/export/componentExporter";
import { InfoOutlined, Warning } from "@mui/icons-material";

const DeductibleExpenseSummary = ({
  deductibleAllocationRows = [],
  totalDeductibleExpenses,
  finalAllowedExpenses,
  deductibleRowOffsets = {},
  rowOffsetKey,
  onDeductibleRowOffsetChange,
  canEdit = true,
  yearOfAssessment,
  displayAndFinalTotalMismatch = false,
}) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));

  const exportRef = useRef(null);
  const handleExportAsImage = async (e) => {
    e.stopPropagation();
    if (exportRef.current) {
      await exportComponentAsImage(
        exportRef.current,
        "Deductible Expense Summary"
      );
    }
  };

  const getKey = (row, index) =>
    typeof rowOffsetKey === "function"
      ? rowOffsetKey(row, index)
      : row?.rowId != null
        ? String(row.rowId)
        : `__idx_${index}`;

  const showOffsetColumn =
    canEdit && typeof onDeductibleRowOffsetChange === "function";

  if (!deductibleAllocationRows || deductibleAllocationRows.length === 0) {
    return null;
  }

  return (
    <Paper
      ref={exportRef}
      elevation={0}
      sx={{
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
        overflow: "hidden",
        bgcolor: (theme) => alpha(theme.palette.background.paper, 0.98),
        boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 3,
          borderBottom: "1px solid",
          borderColor: "divider",
          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            justifyContent: "space-between",
            flexWrap: "wrap",
          }}
        >
          <Typography variant='h6' fontWeight={600} color='primary.main'>
            Deductible Expense Summary
            {yearOfAssessment && ` - Year of Assessment ${yearOfAssessment}`}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Tooltip
              title={
                <div style={{ lineHeight: 1.5 }}>
                  <strong>Deductible Expense = </strong>
                  <br />
                  (Income / Total Income) × Total Deductible Expenses
                </div>
              }
              arrow
              placement='top'
            >
              <IconButton
                color='inherit'
                aria-label='Deductible expense allocation formula'
                sx={{ color: "info.main", fontSize: 20 }}
              >
                <InfoOutlined />
              </IconButton>
            </Tooltip>
            <CameraExportButton
              onClick={handleExportAsImage}
              tooltipTitle='Screenshot of the Deductible Expense Summary'
            />
          </Box>
        </Box>
        <Typography
          variant='caption'
          color='text.secondary'
          sx={{ mt: 0.5, display: "block" }}
        >
          Note: Per-row rounding offset (−1, 0, or +1) adjusts each allocated
          amount. The total is the sum of adjusted row amounts.
        </Typography>
      </Box>

      {/* Summary Table */}
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow
              sx={{
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
              }}
            >
              <TableCell
                sx={{
                  fontWeight: 600,
                  fontSize: isSmallScreen ? "0.875rem" : "1rem",
                }}
              >
                Investment Income
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 600,
                  fontSize: isSmallScreen ? "0.875rem" : "1rem",
                }}
                align='right'
              >
                Income Amount
              </TableCell>
              {showOffsetColumn && (
                <TableCell
                  sx={{
                    fontWeight: 600,
                    fontSize: isSmallScreen ? "0.875rem" : "1rem",
                    width: 88,
                  }}
                  align='right'
                >
                  Offset
                </TableCell>
              )}
              <TableCell
                sx={{
                  fontWeight: 600,
                  fontSize: isSmallScreen ? "0.875rem" : "1rem",
                  width: 200,
                }}
                align='right'
              >
                Deductible Expense
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {deductibleAllocationRows.map((row, index) => {
              const key = getKey(row, index);
              const rawOffset = deductibleRowOffsets[key];
              const offset = Math.max(
                -1,
                Math.min(1, parseInt(rawOffset, 10) || 0)
              );

              return (
                <TableRow
                  key={key}
                  sx={{
                    "&:hover": {
                      bgcolor: (theme) =>
                        alpha(theme.palette.primary.main, 0.04),
                    },
                  }}
                >
                  <TableCell
                    sx={{
                      fontWeight: 500,
                    }}
                  >
                    {`${row.taggedTo ?? ""} - ${
                      row.description || "Untitled item"
                    }`}
                  </TableCell>
                  <TableCell align='right'>
                    {formatAmount(row?.original_amount || row.incomeAmount)}
                  </TableCell>
                  {showOffsetColumn && (
                    <TableCell align='right' sx={{ py: 1 }}>
                      <TextField
                        size='small'
                        select
                        value={offset}
                        onChange={(e) =>
                          onDeductibleRowOffsetChange(
                            key,
                            parseInt(e.target.value, 10)
                          )
                        }
                        sx={{ minWidth: 72 }}
                        SelectProps={{ MenuProps: { disableScrollLock: true } }}
                      >
                        <MenuItem value={-1}>-1</MenuItem>
                        <MenuItem value={0}>0</MenuItem>
                        <MenuItem value={1}>+1</MenuItem>
                      </TextField>
                    </TableCell>
                  )}
                  <TableCell
                    align='right'
                    sx={{
                      fontWeight: 500,
                    }}
                  >
                    {formatAmount(row.deductibleAmount)}
                  </TableCell>
                </TableRow>
              );
            })}

            {/* Total Row */}
            <TableRow
              sx={{
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
                borderTop: "2px solid",
                borderColor: (theme) => alpha(theme.palette.divider, 0.3),
              }}
            >
              <TableCell
                sx={{
                  fontWeight: 700,
                  color: "primary.main",
                }}
              >
                Total Allocated Deductible Expenses
              </TableCell>
              <TableCell></TableCell>
              {showOffsetColumn && <TableCell />}
              <TableCell
                align='right'
                sx={{
                  fontWeight: 700,
                  color: "primary.main",
                }}
              >
                {displayAndFinalTotalMismatch && (
                  <Tooltip
                    title={`Rounding Mismatch Detected. The sum of your individual deductible expenses ${formatAmount(totalDeductibleExpenses)} does not match the expected total ${formatAmount(finalAllowedExpenses)}. This is likely due to decimal rounding in the underlying rows.`}
                  >
                    <Warning
                      sx={{
                        fontSize: 16,
                        verticalAlign: "middle",
                        marginRight: 0.5,
                        color: "error.main",
                      }}
                    />
                  </Tooltip>
                )}
                {formatAmount(totalDeductibleExpenses)}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default DeductibleExpenseSummary;
